import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { PhotoCoverageRow, usePhotoCoverageRows } from "../model/use-photo-coverage-rows";
import { useReportHyperlinks } from "../model/use-report-hyperlinks";
import { compareIds, entityNumber, numberColumn, textCell } from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

/** A report row, or the totals line that closes the exports. */
type PhotoCoverageExportRow = PhotoCoverageRow & { isTotal?: boolean };

const percent = (row: PhotoCoverageRow) =>
  row.total ? Math.round((row.withPhoto / row.total) * 100) : 0;

/** «Покрытие вывоза фотоматериалом» (AIS 15.2.1): each driver's share of jobs with a photo. */
export const PhotoCoverageReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => {
    const t = useT();
    const { rows, isLoading, error } = usePhotoCoverageRows(props.startDate, props.endDate);
    const { driverCell, entityCards } = useReportHyperlinks();

    const columns: Column<PhotoCoverageExportRow>[] = useMemo(
      () => [
        numberColumn(
          t(Namespace.Analytics, "columns.number"),
          t(Namespace.Analytics, "collection.totals.label"),
        ),
        {
          id: "id",
          header: t(Namespace.Analytics, "columns.id"),
          accessor: (row) => (row.isTotal ? "" : entityNumber("user", row.driverId)),
          width: 100,
          sortable: true,
          sortFn: (a, b) => compareIds(a.driverId, b.driverId),
        },
        {
          id: "company",
          header: t(Namespace.Analytics, "photo.company"),
          accessor: (row) => textCell(row.serviceProviderName, row.isTotal),
          width: 220,
          sortable: true,
        },
        {
          id: "driver",
          header: t(Namespace.Analytics, "collection.driver"),
          accessor: (row) => textCell(row.driverName, row.isTotal),
          width: 220,
          sortable: true,
          cell: (info) => driverCell(info.row.original.driverId, info.row.original.driverName),
        },
        {
          id: "withPhoto",
          header: t(Namespace.Analytics, "photo.withPhoto"),
          accessor: (row) => row.withPhoto,
          width: 140,
          sortable: true,
          sortFn: (a, b) => a.withPhoto - b.withPhoto,
        },
        {
          id: "total",
          header: t(Namespace.Analytics, "photo.total"),
          accessor: (row) => row.total,
          width: 140,
          sortable: true,
          sortFn: (a, b) => a.total - b.total,
        },
        {
          id: "percent",
          header: t(Namespace.Analytics, "photo.percent"),
          accessor: (row) => `${percent(row)}%`,
          width: 150,
          sortable: true,
          sortFn: (a, b) => percent(a) - percent(b),
        },
      ],
      [t, driverCell],
    );

    const exportRows = useMemo<PhotoCoverageExportRow[]>(
      () =>
        rows.length
          ? [
              ...rows,
              {
                isTotal: true,
                driverId: 0n,
                driverName: "",
                serviceProviderName: "",
                withPhoto: rows.reduce((sum, r) => sum + r.withPhoto, 0),
                total: rows.reduce((sum, r) => sum + r.total, 0),
              },
            ]
          : rows,
      [rows],
    );

    return (
      <ReportTable
        {...props}
        exportRef={ref}
        settingsKey="photo_coverage_report"
        reportName={t(Namespace.Analytics, "reports.photoCoverage")}
        columns={columns}
        rows={rows}
        exportRows={exportRows}
        rowKey={(row) => String(row.driverId)}
        isLoading={isLoading}
        error={error}
      >
        {entityCards}
      </ReportTable>
    );
  },
);
